import { useContext, useEffect, useState } from "react";
import { RegistrationContext } from '../../Context/RegistrationContext';
import Input from "./Input";
import { showToast } from '../../Components/Toaster/Toaster';
import { getGSTInfo } from "../../lib/utils/apiCalls";
import correctIcon from "../../Assets/correct.svg";
import axios from "axios";
import { verifyEmail } from "../../lib/utils/utils";
import Error from "../Error/Error.Page";
import RegistrationLayout from "../../Components/RegistrationLayout/RegistrationLayout";
import { useNavigate } from "react-router-dom";

function SectionComponent() {
    const {
        gstDetails,
        setGstDetails,
        gstVerified,
        setGstVerified,
        gstCardInfo,
        setGstCardInfo,
        isSameEmail,
        setIsSameEmail,
        fetchedEmail,
        gstDetailsError,
        setGstDetailsError,
        defaultGSTDetailsError
    } = useContext(RegistrationContext);

    const navigate = useNavigate();

    async function verifyGST() {
        if(gstDetails.gstIn) {
            try {
                const response = await getGSTInfo({ gstinNumber: gstDetails.gstIn });
                
                if (response.status === 200 && response.data?.statusCode === "10000") {
                    setGstCardInfo(response.data.data);
                    setGstVerified(true);
                    setGstDetailsError({
                        ...gstDetailsError,
                        gstIn: '',
                    });
                } else {
                    setGstDetailsError({
                        ...gstDetailsError,
                        gstIn: 'Please enter a valid GST Number.',
                    });
                }
            }
            catch (err) {
                setGstDetailsError({
                    ...gstDetailsError,
                    gstIn: err.message || 'An error occurred while verifying GST.',
                });
            }
        }
        else {
            setGstDetailsError({
                ...gstDetailsError,
                gstIn: 'Please enter a GST Number.',
            });
        }
    }

    function handleChange(e) {
        const { name, value } = e.target;
        setGstDetails({ ...gstDetails, [name]: value })
    }

    function handleKeyDown(e) {
        if(e.key === 'Enter') {
            verifyGST();
        }
    }

    useEffect(() => {
        if(isSameEmail) {
            if(fetchedEmail) {
                setGstDetails({
                    ...gstDetails,
                    ownerEmail: fetchedEmail,
                });
            }
            else {
                showToast('Invite expired. Please request for invite again.', 'error');
            }
        }
        // eslint-disable-next-line
    }, [isSameEmail]);

    function handleNext() {
        const validationErrors = {};

        if(!gstDetails.ownerName) {
            validationErrors.ownerName = 'Owner name is required';
        }

        if(!gstDetails.ownerEmail) {
            validationErrors.ownerEmail = 'Owner email is required';
        }
        else if(!verifyEmail(gstDetails.ownerEmail)) {
            validationErrors.ownerEmail = 'Enter a valid email';
        }

        if(!fetchedEmail) {
            showToast('Invite expired. Please request for invite again.', 'error');
            return;
        }

        if(Object.keys(validationErrors).length === 0) {
            setGstDetailsError(defaultGSTDetailsError);
            navigate('/storesetup');
        }
        else {
            setGstDetailsError(validationErrors);
        }
    }

    return (
        <div className="flex flex-col gap-6 w-full">
            <h1 className="font-semibold text-[24px] leading-[44px] text-text1">Business Information</h1>

            <div className="flex flex-col gap-8 w-full">
                <div className="flex flex-col gap-6 w-full">
                    <div className="w-full flex flex-col gap-3">
                        <div className="flex flex-col gap-2">
                            <div className="flex items-center justify-between">
                                <label htmlFor="gstIn" className="font-medium text-base text-text1">GSTIN Verification</label>

                                {
                                    gstDetailsError?.gstIn?.length > 0 && 
                                    <p className='text-[14px] leading-5 text-[#DC2626] select-none'>{gstDetailsError.gstIn}</p>
                                }
                            </div>

                            <div className="w-full select-none px-3 py-4 flex items-center gap-[10px] rounded-[9px] bg-[#F5F5F5] border border-[#EAEAEA]">
                                <input 
                                    id='gstIn' 
                                    name='gstIn' 
                                    type="text" 
                                    value={gstDetails.gstIn} 
                                    className="grow outline-none border-none bg-transparent text-text1 placeholder:text-[#666666]" 
                                    placeholder="enter GSTIN number" 
                                    onChange={handleChange} 
                                    onKeyDown={handleKeyDown} 
                                    disabled={gstVerified}
                                />

                                <button onClick={verifyGST} className={`text-base ${gstVerified ? 'text-primaryColor font-semibold' : 'text-[#999999] font-normal'} outline-none`}>Verify</button>
                            </div>
                        </div>

                        {
                            gstVerified && 
                            <>
                                <Input 
                                    labelText='Owner Name' 
                                    errorText={gstDetailsError.ownerName} 
                                    inputOptions={{
                                        type: 'text',
                                        id: 'ownerName',
                                        name: 'ownerName',
                                        value: gstDetails.ownerName,
                                        onChange: handleChange,
                                        placeholder: 'enter owner name'
                                    }}
                                />

                                <Input 
                                    labelText='Enter Owner Email ID' 
                                    errorText={gstDetailsError.ownerEmail} 
                                    inputOptions={{
                                        type: 'text',
                                        id: 'ownerEmail',
                                        name: 'ownerEmail',
                                        value: gstDetails.ownerEmail,
                                        onChange: handleChange,
                                        placeholder: 'enter owner email address'
                                    }}
                                />

                                <div className="select-none">
                                    <input 
                                        type="checkbox" 
                                        id='same-email' 
                                        name='same-email' 
                                        checked={isSameEmail} 
                                        onChange={() => setIsSameEmail(!isSameEmail)} 
                                        className="hidden"
                                    />

                                    <label htmlFor="same-email" className="cursor-pointer font-normal text-[14px] leading-6 text-text1 flex items-center gap-2">
                                        <div className={`size-6 ${isSameEmail ? 'bg-primaryColor' : 'bg-transparent'} flex items-center justify-center rounded-md border border-[#D1D5DB]`}>
                                            {
                                                isSameEmail && 
                                                <img src={correctIcon} alt="" />
                                            }
                                        </div>
                                        Use same registered email address
                                    </label>
                                </div>
                            </>
                        }
                    </div>

                    {
                        gstVerified && 
                        <div className="w-full bg-[#F5F5F5] p-5 flex flex-col gap-6 rounded-lg shadow-[0px_0px_4px_0px_#00000040]">
                            <h2 className="font-semibold text-[18px] leading-6 text-text1">Gstin Information</h2>

                            <div className="w-full flex gap-6 font-medium text-base">
                                <div className="flex flex-col gap-5 w-1/4 text-text1">
                                    <h3>Businees Name</h3>

                                    <h3>Pan Number</h3>

                                    <h3>Address</h3>
                                </div>

                                <div className="flex flex-col gap-5 w-3/4 text-text2">
                                    <p>{gstCardInfo.legalname}</p>

                                    <p>{gstCardInfo.panNumber}</p>
                                    
                                    <p>
                                        {[ 
                                            gstCardInfo.addressTotal?.building,
                                            gstCardInfo.addressTotal?.floor,
                                            gstCardInfo.addressTotal?.street,
                                            gstCardInfo.addressTotal?.district,
                                            gstCardInfo.addressTotal?.state && gstCardInfo.addressTotal?.zip 
                                            ? `${gstCardInfo.addressTotal.state} - ${gstCardInfo.addressTotal.zip}` 
                                            : gstCardInfo.addressTotal?.state || gstCardInfo.addressTotal?.zip
                                        ]
                                        .filter(Boolean)
                                        .join(', ')}
                                    </p>
                                </div>
                            </div>
                        </div>
                    }
                </div>

                {
                    gstVerified && 
                    <button onClick={handleNext} className="w-full bg-primaryColor hover:bg-primaryAccent text-white px-3 py-4 rounded-[9px] font-semibold text-[18px] leading-6">Next</button>
                }
            </div>
        </div>
    )
}

function BusinessInformation() {
    const [error, setError] = useState(false);

    const {
        gstDetails,
        setGstDetails,
        setFetchedEmail,
    } = useContext(RegistrationContext);

    async function fetchEmailFromToken(token) {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}v1/verifyEmail/${token}`);
            // eslint-disable-next-line eqeqeq
            if (response?.data?.statusCode == '10000') {
                setFetchedEmail(response.data.data);
                setGstDetails({
                    ...gstDetails,
                    email: response.data.data,
                });
            }
            // eslint-disable-next-line eqeqeq
            else if(response.data.statusCode == '10001') {
                showToast("Failed to fetch email from token", "error");
            }
        }
        catch (error) {
            showToast(error, 'error');
        }
    }

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get("token");

        if(!token) {
            showToast("Token not available.", "error");
            setError(true);
            return;
        }
        else {
            fetchEmailFromToken(token);
        }
        // eslint-disable-next-line
    }, []);

    if(error) {
        return <Error />
    }

    return (
        <RegistrationLayout>
            <SectionComponent></SectionComponent>
        </RegistrationLayout>
    )
}

export default BusinessInformation;
